import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Grid from '@mui/material/Grid';

import { DialogTitleWithClose } from 'ui';
import PrivacyPolicyPdf from 'assets/pdfs/privacy-policy.pdf';

const PrivacyPolicyModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitleWithClose onClose={onClose}>&nbsp;</DialogTitleWithClose>
      <DialogContent>
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              href={PrivacyPolicyPdf}
              download="privacy-policy.pdf"
            >
              View as PDF
            </Button>
          </Grid>
        </Grid>
        <DialogContentText variant="h5">{t('ScopeOne Privacy Policy')}</DialogContentText>
        <DialogContentText>
          {t(
            'This website ("ScopeOne") is operated by Scope SE & Co. KGaA, Lennéstraße 5, 10785 Berlin, Germany ("Scope" or "we"). Scope acts as the data controller.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t('This Privacy Policy explains the basis upon which we collect and use personal data.')}
        </DialogContentText>
        <DialogContentText>
          {t(
            'Personal data means any information which relates to a specific person (“Personal Data”).',
          )}
        </DialogContentText>
        <DialogContentText>
          {t(
            'This policy sets out the basis on which any Personal Data we collect from you, or that you provide to us, will be processed by us. Please read the following carefully to understand our views and practices regarding your personal data and how we will treat it.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">
          {t('1. Personal Data Collected During Registration')}
        </DialogContentText>
        <DialogContentText>
          {t(
            'We request Personal Data from you when you register on ScopeOne (e.g., your first name, last name, e-mail address, job title, business address, phone number, or other identifiers)',
          )}
          .
        </DialogContentText>
        <DialogContentText variant="h5">{t('2. Use of ScopeOne')}</DialogContentText>
        <DialogContentText>
          {t(
            'During the use of ScopeOne, we only collect and use the data that your Internet browser automatically transmits to us, such as for example',
          )}
          :
        </DialogContentText>
        <DialogContentText component="div">
          <ul>
            <li>{t('Date and time of access to one of our Internet pages')}</li>
            <li>{t('your browser type')}</li>
            <li>{t('the browser settings')}</li>
            <li>{t('the operating system used')}</li>
            <li>{t('your last visited page')}</li>
            <li>
              {t(
                'the amount of data transferred and the access status (file transferred, file not found etc.)',
              )}
            </li>
            <li>{t('the loading times of our website and')}</li>
            <li>{t('your IP address.')}</li>
          </ul>
        </DialogContentText>
        <DialogContentText>
          {t(
            'We collect and use this data in order to enable the use of ScopeOne for statistical and marketing purposes and to improve our internet offer.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('3. Cookies')}</DialogContentText>
        <DialogContentText>
          {t(
            'For our website we use cookies. Cookies are small text files that are sent to your browser by our web server during your visit to our website and stored on your device for later retrieval by our web server.',
          )}
        </DialogContentText>
        <DialogContentText>{t('We use the following cookies on our websites')}:</DialogContentText>
        <DialogContentText>
          {t(
            'When you visit ScopeOne, we may recognize you and customize your use of ScopeOne accordingly (e.g., once you have registered, a cookie will be used to avoid asking you to register again). The information obtained from using these technologies is not sold or otherwise made available to third parties except in accordance with this policy.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t(
            'You can determine whether cookies can be set and retrieved using the settings in your browser. For example, you can completely deactivate the storage of cookies in your browser, restrict it to certain web pages or configure your browser in such a way that it automatically notifies you as soon as a cookie is to be set and asks you for feedback. You can block or delete individual cookies. For technical reasons, however, this may result in some of the functions of our website being impaired and no longer functioning completely.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('4. Use of Your Personal Data')}</DialogContentText>
        <DialogContentText>
          {t(
            'We use Personal Data that we collect solely for our internal use, including for statistical analysis of users behaviour, for product development, for content improvement or to customize the content and layout of our Properties. We may, in our sole discretion, use Personal Data to contact you regarding (i) your account status and changes to the subscriber agreement, (ii) this Policy, (iii) any other policies, agreements or transactions relevant to your use of ScopeOne and (iv) in accordance with applicable laws, marketing to you products and services which we believe may be of interest to you.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t(
            'You explicitly consent to be contacted by Scope for marketing purposes via telephone, e-mail and SMS.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('5. Right of revocation')}</DialogContentText>
        <DialogContentText>
          {t(
            'We draw your attention to the fact that you can revoke any consent granted to us under data protection law at any time with effect for the future.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t('To do so, please contact us by e-mail: [privacy@scopegroup.com ]')}
        </DialogContentText>
        <DialogContentText variant="h5">
          {t('6. Involvement of Service Providers and Transfer to Third Parties')}
        </DialogContentText>
        <DialogContentText>
          {t(
            'For the provision of this website and for the aforementioned purposes, your data will be passed on to technical service providers (e. g. website hosting, support, quality assurance or mailing service), which we have of course carefully selected and commissioned in writing.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t(
            'These service providers are bound to our instructions and are regularly monitored by us.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('7. Storage of Data')}</DialogContentText>
        <DialogContentText>
          {t('Your Personal Data is stored in the European Union.')}
        </DialogContentText>
        <DialogContentText>
          {t(
            'We will keep your Personal Data for the length of time required for the specific purposes for which it was collected, which are set out in this Privacy Policy.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('8. Data Security')}</DialogContentText>
        <DialogContentText>
          {t(
            'We have established reasonable precautions to protect collected Personal Data from loss, misuse, unauthorized access, disclosure, alteration or destruction, which include contractual, administrative, physical, and technical steps. Technical measures include use of firewall protection and encryption technology where appropriate. However, no data transmission over the Internet or other network can be guaranteed to be 100% secure. As a result, while we strive to protect information transmitted on or through the Properties or services, we cannot and do not guarantee the security of any information you transmit on or through the Properties or services, and you do so at your own risk.',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">{t('9. Contact Information')}</DialogContentText>
        <DialogContentText>
          {t(
            'Questions or concerns regarding our privacy policy and data protection practices should be addressed to:',
          )}
        </DialogContentText>
        <DialogContentText variant="h5">
          {t('Scope’s Appointed Data Protection Officer:')}
        </DialogContentText>
        <DialogContentText>
          Christian Werner
          <br />
          Scope SE & Co. KGaA
          <br />
          Lennéstraße 5<br />
          D-10785 Berlin
        </DialogContentText>
        <DialogContentText>
          Phone +49 30 27891-0
          <br />
          Fax +49 30 27891-100
          <br />
          privacy@scopegroup.com
          <br />
          www.scopegroup.com
        </DialogContentText>
        <DialogContentText variant="h5">Data controller:</DialogContentText>
        <DialogContentText>
          Scope SE & Co. KGaA
          <br />
          Lennéstraße 5<br />
          D-10785 Berlin
          <br />
          <br />
          {t('Phone')} +49 30 27891-0
          <br />
          {t('Fax')} +49 30 27891-100
          <br />
          privacy@scopegroup.com
          <br />
          www.scopegroup.com
          <br />
          <br />
          {t('Represented by the general partner Scope Management SE')}
          <br />
          {t(
            'Executive Board: Florian Schoeller (Chairman & CEO), Guillaume Jolivet (CAO), Matthias Böhm (COO), Alexander Bergé (CFO)',
          )}
          <br />
          {t('Chair of the Supervisory Board: Inès de Dinechin')}
          <br />
          {t('Commercial Register Berlin: HRB 178531 B')}
          <br />
        </DialogContentText>
        <DialogContentText variant="h5">{t('10. Your Rights')}</DialogContentText>
        <DialogContentText>
          {t(
            'You have the right to request access to and rectification or erasure of your Personal Data or to restrict processing of your Personal Data or to object to processing as well as the right to data portability.',
          )}
        </DialogContentText>
        <DialogContentText>
          {t('Further, you have the right to lodge a complaint with a supervisory authority.')}
        </DialogContentText>
        <DialogContentText>
          {t('The competent supervisory authority for Scope is:')}
        </DialogContentText>
        <DialogContentText>
          {t('Berliner Beauftragte für Datenschutz und Informationsfreiheit')}
          <br />
          Friedrichstr. 219
          <br />
          D-10969 Berlin
          <br />
          Germany
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

PrivacyPolicyModal.defaultProps = {
  open: false,
  onClose: () => null,
};

PrivacyPolicyModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default PrivacyPolicyModal;
